import { createContext, useContext, MutableRefObject } from "react";

import { ResolvedSnapPoints, SnapPoint } from "./snapPoints";

export type OpenChangeReason =
  | "trigger-press"
  | "backdrop-press"
  | "escape-key"
  | "close-press"
  | "swipe-down";

export type BottomSheetContextValue = {
  open: boolean;
  setOpen: (open: boolean, reason: OpenChangeReason) => void;
  mounted: boolean;
  setMounted: (mounted: boolean) => void;
  dragging: boolean;
  setDragging: (dragging: boolean) => void;
  titleId: string | undefined;
  setTitleId: (id: string | undefined) => void;
  descriptionId: string | undefined;
  setDescriptionId: (id: string | undefined) => void;
  popupRef: MutableRefObject<HTMLDivElement | null>;
  backdropRef: MutableRefObject<HTMLDivElement | null>;
  dismissible: boolean;
  modal: boolean;
  previousFocusRef: MutableRefObject<HTMLElement | null>;
  snapPoints: ResolvedSnapPoints | null;
  snapPoint: SnapPoint | undefined;
  setSnapPoint: (snapPoint: SnapPoint) => void;
  activeSnapIndex: number;
  activeSnapOffset: number;
  keyboardOpen: boolean;
  /** True when this Root is nested under another BottomSheet.Root. */
  isNested: boolean;
  /** Nest depth (0 = root). Used for backdrop/popup stacking order. */
  nestDepth: number;
  /** Direct nested sheets currently open (Escape / focus / parent drag). */
  nestedOpenCount: number;
  /** Invoked by a nested Root when it opens or closes. */
  onNestedOpenChange: (open: boolean) => void;
  /** Invoked by a nested sheet while the user drags it (dismiss progress 0–1). */
  onNestedDrag: (percentageDragged: number) => void;
  /** Invoked when a nested sheet gesture ends; `open` is whether it stayed open. */
  onNestedRelease: (open: boolean) => void;
  /** Wired at Root from the parent context so Popup/gestures can notify upward. */
  notifyParentNestedDrag: ((percentageDragged: number) => void) | undefined;
  notifyParentNestedRelease: ((open: boolean) => void) | undefined;
};

const BottomSheetContext = createContext<BottomSheetContextValue | null>(null);

export function useBottomSheetContext(): BottomSheetContextValue {
  const context = useContext(BottomSheetContext);
  if (!context) {
    throw new Error("BottomSheet parts must be used within BottomSheet.Root");
  }
  return context;
}

export function useOptionalBottomSheetContext(): BottomSheetContextValue | null {
  return useContext(BottomSheetContext);
}

export default BottomSheetContext;
